/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import FormUtilityMixin from 'app/mixins/FormUtilityMixin.js';
import ConstantMixin from 'app/mixins/ConstantMixin.js';
import * as _ from 'underscore';
import Utilities from 'app/utility/Utilities.js';
import DestinationOverrideDialog from '@/shadcn/components/settings/destination-override-dialog.tsx';
import {
    buildOverrideSections,
    destinationNetwork
} from '@/lib/social-settings-sections.ts';
import {
    dirtySectionIds,
    sectionErrorCounts,
    sectionPaths,
    validationKeys,
    validationTree
} from '@/lib/settings-sections.ts';
import {
    overrideResetWrites,
    overrideWrites
} from '@/lib/settings-changes.ts';
import { SOCIAL_ERROR_KEYS } from '@/lib/social-settings-sections.ts';

/**
 * The tab a URL names, and the URL a tab wants — except there is no URL here.
 * A modal keeps its tab in its own state, which is what legacy does too
 * (`setActivePanel`), so this is only the section-id translation.
 */
const SECTION_BY_PANEL = {
    options: 'post-options',
    advanced: 'advanced'
};

const PANEL_BY_SECTION = _.invert(SECTION_BY_PANEL);

const getOutput = (props) => {
    for (let i = 0; i < props.automatedRoutes.list.length; ++i) {
        for (let j = 0; j < props.automatedRoutes.list[i].outputs.length; ++j) {
            if (parseInt(props.automatedRoutes.list[i].outputs[j].id, 10) === parseInt(props.outputID, 10)) {
                return props.automatedRoutes.list[i].outputs[j];
            }
        }
    }
    return null;
};

class OutputConfigModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            output: {},
            social: {},
            socialDefaults: null,
            activePanel: 'options',
            errors: null,
            isDirty: false
        };

        this.saveOutput = this.saveOutput.bind(this);
        this.trimParams = this.trimParams.bind(this);
        this.setActivePanel = this.setActivePanel.bind(this);
        this.onFieldChange = this.onFieldChange.bind(this);
        this.selectSection = this.selectSection.bind(this);
        this.resetToGlobal = this.resetToGlobal.bind(this);

    }

    static getDerivedStateFromProps(props, state) {
        let nextState = {};

        // update social in state when details come back
        if (props.socials.list.length) {
            for (let i = 0; i < props.socials.list.length; ++i) {
                if (parseInt(props.socials.list[i].id, 10) === parseInt(props.socialID, 10) && props.socials.list[i].params !== undefined) {
                    nextState.social = Utilities.deepClone(props.socials.list[i]);
                    break;
                }
            }
        }

        if (props.automatedRoutes.list.length) {
            if (_.isEmpty(state.output)) {
                let output = getOutput(props);
                if (output && output.paParams !== undefined) {
                    nextState.output = output;
                }
            }
        }

        return (Object.keys(nextState).length) ? nextState : null;

    }

    componentDidMount() {

        this.props.getOutput(this.props.outputID);
        this.props.getSocial(this.props.socialID);
        this.props.deleteApiError(['OUTPUT_DELETE', 'OUTPUT_UPDATE', 'SOCIAL_UPDATE']);

    }

    componentDidUpdate(prevProps, prevState) {

        let errors = [];

        errors = this.props.getApiErrors(['OUTPUT_DELETE', 'OUTPUT_UPDATE', 'SOCIAL_UPDATE']);

        if (errors.length && this.state.errors === null) {
            if (this.props.getPermErrors().length) {
                this.props.showUpgradeModal(this.props.closeAllModals, null, errors, 'social_error');
            }
            this.setState({
                errors
            });
        }

        if (((prevProps.app.loading.OUTPUT_UPDATE === true && this.props.app.loading.OUTPUT_UPDATE === false) ||
            (prevProps.app.loading.OUTPUT_DELETE === true && this.props.app.loading.OUTPUT_DELETE === false)) &&
            errors.length === 0) {
            this.props.closeAllModals();
        }

    }


    /**
     * Reduce the override to only the params the user actually changed.
     *
     * WHY: the first edit has to clone the social's whole params object so the
     * field being edited has somewhere to write, which meant we SAVED the whole
     * clone - a full snapshot. From then on the automation ignored every later
     * change to the social's Global Settings, not just the field that was
     * edited, because every key was present in its own override.
     *
     * The backend already merges: OutputRule::getParams() returns
     * array_merge($pa->getParams(), unserialize($or_paParams)), so any key we
     * leave out keeps tracking the social. Sending only the differences is
     * therefore all that was missing.
     *
     * Returns null when nothing differs, which is the same thing
     * toggleDefaultUsage does and means "use the defaults".
     */
    trimParams(paParams) {
        let base = (this.state.social && this.state.social.params) ? this.state.social.params : {},
            diff = {},
            same;

        if (!paParams) {
            return paParams;
        }

        // The UI stores numbers as strings in places, so compare loosely for
        // primitives and structurally for the nested ones (gmb, playLists,
        // hashTags), rather than dropping a real change as equal.
        same = function (a, b) {
            if (a === b) {
                return true;
            }
            if (a === null || b === null || a === undefined || b === undefined) {
                return false;
            }
            if (typeof a === 'object' || typeof b === 'object') {
                try {
                    return JSON.stringify(a) === JSON.stringify(b);
                } catch (err) {
                    return false;
                }
            }
            return String(a) === String(b);
        };

        Object.keys(paParams).forEach(function (key) {
            if (!same(paParams[key], base[key])) {
                diff[key] = paParams[key];
            }
        });

        return Object.keys(diff).length ? diff : null;
    }

    saveOutput() {

        let output = getOutput(this.props);

        if (parseInt(output.activeFlag, 10) === 0 &&
            parseInt(this.state.output.activeFlag, 10) === 1 &&
            parseInt(this.state.social.activeFlag, 10) === 0) {
            let social = Utilities.deepClone(this.state.social);
            social.activeFlag = 1;
            this.props.updateSocial(social);
        }

        // clear out any previous errors
        this.props.deleteApiError(['OUTPUT_CREATE', 'OUTPUT_UPDATE']);

        // update - with the override reduced to what actually differs, so
        // every untouched setting keeps following the social. See trimParams.
        let payload = Utilities.deepClone(this.state.output);

        payload.paParams = this.trimParams(payload.paParams);

        this.props.updateOutput(payload);

    }

    /**
     * Every field-level edit on the override.
     *
     * `overrideWrites` decides what to store, including the seed: `paParams`
     * is null while the automation inherits, so the first edit has to clone the
     * destination's params for the field to land in. That clone is reduced back
     * to real differences by `trimParams` on save, so it never becomes a
     * permanent fork.
     *
     * The writes are replayed in order through the same `requestChange` legacy
     * uses, which mutates state in place — so the seed is present by the time
     * the field's own write walks the path into it.
     */
    onFieldChange(change) {

        let writes = overrideWrites(change, this.state.output, this.state.social);

        if (!writes.length) {
            return;
        }

        writes.forEach((entry) => {
            FormUtilityMixin.requestChange.call(this, {
                currentTarget: {dataset: {field: entry.field}},
                target: {value: entry.value}
            });
        });

        this.setState({isDirty: true});

    }

    selectSection(id) {

        this.setActivePanel({currentTarget: {dataset: {panel: PANEL_BY_SECTION[id] || 'options'}}});

    }

    /** Hand every setting back to the destination. Legacy's toggleDefaultUsage. */
    resetToGlobal() {

        overrideResetWrites().forEach((entry) => {
            FormUtilityMixin.requestChange.call(this, {
                currentTarget: {dataset: {field: entry.field}},
                target: {value: entry.value}
            });
        });

        this.setState({isDirty: true});

    }

    setActivePanel(e) {
        let activePanel = e.currentTarget.dataset.panel;

        if (activePanel) {
            this.setState({
                activePanel
            });
        }
    }

    render() {

        let constants = this.props.app.config.constants,
            social = this.state.social,
            output = this.state.output,
            loading = _.isEmpty(social) || social.params === undefined || _.isEmpty(output),
            complex = this.props.app.flags.routeViewType === constants.ROUTE_VIEW_ADVANCED,
            scope = complex ? 'route' : 'automation',
            sections = buildOverrideSections(output, social, {
                constants,
                feedUrlBase: this.props.app.config.feedUrl,
                shorteners: [],
                domains: [],
                shortenServiceName: ConstantMixin.getShortenServiceByCode,
                categories: this.props.socials.categories || {},
                playlists: this.props.socials.playlists || {},
                validation: validationTree(this.props.app),
                /* H5 is a data rule: a social source composes its own text and
                   media, so the four content switches are swapped for one
                   message rather than shown and ignored. */
                sourceType: this.sourceKey(),
                scope
            }),
            paths = sectionPaths(sections, 'output', {});

        return (
            <DestinationOverrideDialog
                sections={sections}
                network={destinationNetwork(social)}
                accountName={social.name || ''}
                avatarUrl={social.icon || social.src || undefined}
                scope={scope}
                scopeName={this.props.name || ''}
                /* Derived, never chosen: null paParams IS inheriting. */
                inherit={output.paParams === null || output.paParams === undefined}
                onReset={this.resetToGlobal}
                loading={loading}
                formKey={String(output.id || '')}
                onChange={this.onFieldChange}
                activeSection={SECTION_BY_PANEL[this.state.activePanel] || 'post-options'}
                onSectionChange={this.selectSection}
                dirty={this.state.isDirty}
                /* The stored output is the clean side, which is the same
                   record `saveOutput` diffs against for the activeFlag rule. */
                dirtySections={dirtySectionIds(paths, output, getOutput(this.props) || {})}
                errorCounts={sectionErrorCounts(validationKeys(this.props.app), SOCIAL_ERROR_KEYS)}
                saving={this.props.app.loading.OUTPUT_UPDATE === true}
                formError={this.state.errors !== null
                    ? "We couldn't complete that update. Check the highlighted tabs for details."
                    : undefined}
                /* No "Remove this output from …" link (Emily, 2026-10-01):
                   an output leaves an automation from the automation itself. */
                onSave={this.saveOutput}
                onCancel={this.props.closeAllModals}
                onClose={this.props.closeAllModals} />
        );
    }

    /**
     * The automation's source type as the section builder names it, or
     * undefined for a feed. `type` is handed in by the caller, which passes
     * INPUT_TYPE_FEED for complex routes because they can hold several.
     */
    sourceKey() {

        let constants = this.props.app.config.constants,
            type = parseInt(this.props.type, 10);

        switch (type) {
            case constants.INPUT_TYPE_TWITTER:
                return 'twitter';
            case constants.INPUT_TYPE_INSTAGRAM:
            case constants.INPUT_TYPE_INSTAGRAM_BUSINESS:
                return 'instagram';
            case constants.INPUT_TYPE_FACEBOOK_PAGE:
                return 'facebook';
            case constants.INPUT_TYPE_TIKTOK:
                return 'tiktok';
            case constants.INPUT_TYPE_YOUTUBE:
                return 'youtube';
            default:
                return undefined;
        }

    }

};

OutputConfigModal.displayName = "OutputConfigModal";

OutputConfigModal.propTypes = {
    name: PropTypes.string,
    type: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    recordPath: PropTypes.func,
};

OutputConfigModal.defaultProps = {
    name: '',
    type: '',
    recordPath: null,
};

export default OutputConfigModal;
